import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";

export const usePatientHistory = (id: number | string | undefined, isAccountContext: boolean = false) => {
    const endpoints = isAccountContext
        ? {
            consultations: API_ENDPOINTS.PATIENTS.MALADE(Number(id)),
            constantes: API_ENDPOINTS.CONSTANTES.MALADE(Number(id)),
            medicalRecords: API_ENDPOINTS.MEDICAL_RECORDS.BY_PATIENT(Number(id)),
        }
        : {
            consultations: API_ENDPOINTS.PATIENTS.GET_BY_ID(Number(id)),
            constantes: API_ENDPOINTS.CONSTANTES.BY_PATIENT(Number(id)),
            medicalRecords: API_ENDPOINTS.MEDICAL_RECORDS.PATIENT(Number(id)),
        };

    const consultationsQuery = useQuery({
        queryKey: ['patient', 'consultations', id, isAccountContext],
        queryFn: async () => {
            const response = await axiosInstance.get(endpoints.consultations).catch(async () => {
                // Fallback as in original code
                return axiosInstance.get(isAccountContext
                    ? API_ENDPOINTS.PATIENTS.GET_BY_ID(Number(id))
                    : API_ENDPOINTS.PATIENTS.MALADE(Number(id))
                );
            });
            return Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
        },
        enabled: !!id,
    });

    const constantesQuery = useQuery({
        queryKey: ['patient', 'constantes', id, isAccountContext],
        queryFn: async () => {
            const response = await axiosInstance.get(endpoints.constantes).catch(() => ({ data: [] }));
            return Array.isArray(response.data) ? response.data : [];
        },
        enabled: !!id,
    });

    const medicalRecordsQuery = useQuery({
        queryKey: ['patient', 'medical-records', id, isAccountContext],
        queryFn: async () => {
            const response = await axiosInstance.get(endpoints.medicalRecords).catch(() => ({ data: [] }));
            return Array.isArray(response.data) ? response.data : [];
        },
        enabled: !!id,
    });

    return {
        consultations: consultationsQuery.data || [],
        constantes: constantesQuery.data || [],
        medicalRecords: medicalRecordsQuery.data || [],
        isLoading: consultationsQuery.isLoading || constantesQuery.isLoading || medicalRecordsQuery.isLoading,
        isError: consultationsQuery.isError || constantesQuery.isError || medicalRecordsQuery.isError,
        error: consultationsQuery.error || constantesQuery.error || medicalRecordsQuery.error,
        refetch: async () => {
            await Promise.all([
                consultationsQuery.refetch(),
                constantesQuery.refetch(),
                medicalRecordsQuery.refetch()
            ]);
        }
    };
};
